import React, { useCallback, useEffect, useRef, useState } from 'react';
import { icons } from 'lucide-react';
import Icon from '../ui/Icon';
import {
  KIND_META,
  StockHistoryEntry,
  StockItemView,
  StockOverview,
  fetchStockHistoryPage,
  plural,
  stockErrorText,
  undoStockBatch,
  useStockHistory,
} from './api';

/*
 * Klocki Tablicy Magazynu: klawiatura liczbowa, arkusze (historia, produkt, wybór pozycji),
 * okno potwierdzenia, pierścień postępu. Wszystko na klasach „mg-", żeby wyglądało tak samo
 * w kiosku (ciemny, duże cele) i w panelu admina (jasny, gęściej).
 */

/* ---------- pomocnicze ---------- */

/** Ikona produktu z katalogu — nieznana nazwa w lucide nic nie rysuje, więc dajemy zapas. */
export function itemIconName(name: string | null | undefined): string {
  if (!name) return 'package';
  const pascal = name
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  return (icons as Record<string, unknown>)[pascal] ? name : 'package';
}

export const szt = (n: number) => `${n} szt.`;
export const signed = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0');

/** Nazwa główna + podpis (wariant) — własna nazwa z katalogu wygrywa nad „Tytuł (Wariant)". */
export function itemTitle(it: Pick<StockItemView, 'displayName' | 'title'>): string {
  return it.displayName || it.title;
}
export function itemVariant(it: Pick<StockItemView, 'displayName' | 'variantTitle'>): string | null {
  return it.displayName ? null : it.variantTitle;
}

export function timePl(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const hm = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return hm;
  return `${d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })} ${hm}`;
}

/* ---------- przycisk dotykowy bez kradzieży fokusu ---------- */

/** onPointerDown + preventDefault: klawisz nie zabiera fokusu (nie wyskakuje klawiatura ekranowa). */
function press(fn: () => void) {
  return (e: React.PointerEvent) => {
    e.preventDefault();
    fn();
  };
}

/* Dotyk: okno zamknięte na pointerdown znika spod palca, a „click" po puszczeniu trafia w to,
   co leży pod spodem (np. kafelek dostawy dodałby krok po „Gotowe"). Połykamy ten jeden click. */
function swallowGhostClick() {
  const eat = (ev: Event) => { ev.preventDefault(); ev.stopPropagation(); };
  window.addEventListener('click', eat, { capture: true, once: true });
  setTimeout(() => window.removeEventListener('click', eat, { capture: true }), 450);
}
function pressClose(fn: () => void) {
  return (e: React.PointerEvent) => {
    e.preventDefault();
    if (e.pointerType !== 'mouse') swallowGhostClick();
    fn();
  };
}

/* ---------- pierścień postępu ---------- */

export function Ring({ value, total, size = 56, stroke = 7 }: { value: number; total: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(1, value / total) : 0;
  return (
    <svg className="mg-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} className="mg-ring-bg" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        className={`mg-ring-fg${total > 0 && pct >= 1 ? ' full' : ''}`}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={`${c} ${c}`}
        strokeDashoffset={c * (1 - pct)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

/* ---------- arkusz / okno ---------- */

export function Sheet({
  title, sub, onClose, children, wide, icon,
}: { title: React.ReactNode; sub?: React.ReactNode; onClose: () => void; children: React.ReactNode; wide?: boolean; icon?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="mg-scrim" onClick={onClose}>
      <div className={`mg-sheet${wide ? ' wide' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="mg-sheet-grip" />
        <div className="mg-sheet-head">
          {icon && <span className="mg-sheet-ico"><Icon name={icon} size={20} /></span>}
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3>{title}</h3>
            {sub && <div className="mg-sheet-sub">{sub}</div>}
          </div>
          <button type="button" className="mg-x" onClick={onClose} aria-label="Zamknij"><Icon name="x" size={20} /></button>
        </div>
        <div className="mg-sheet-body">{children}</div>
      </div>
    </div>
  );
}

export function Confirm({
  title, desc, confirmLabel, tone = 'primary', onConfirm, onCancel, busy,
}: {
  title: string; desc: React.ReactNode; confirmLabel: string; tone?: 'primary' | 'danger' | 'success';
  onConfirm: () => void; onCancel: () => void; busy?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="mg-scrim center" onClick={onCancel}>
      <div className="mg-dialog" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
        <h3>{title}</h3>
        <div className="mg-dialog-desc">{desc}</div>
        <div className="mg-dialog-actions">
          <button type="button" className="mg-btn ghost" onClick={onCancel}>Wróć</button>
          <button type="button" className={`mg-btn ${tone}`} onClick={onConfirm} disabled={busy}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- klawiatura liczbowa ---------- */

export type KeypadRequest = {
  title: string;
  sub?: string;
  icon?: string;
  /** Wartość startowa — pierwsze naciśnięcie cyfry ją zastępuje (jak zaznaczone pole). */
  initial: number;
  /** Podpowiedzi pod wyświetlaczem, np. „Brakuje 7". */
  suggestions?: Array<{ label: string; value: number }>;
  /** Tekst pod wartością (np. „było 7 → jest 5"). */
  describe?: (n: number) => React.ReactNode;
  confirmLabel?: string;
  onDone: (n: number) => void;
};

export function Keypad({ req, onClose }: { req: KeypadRequest; onClose: () => void }) {
  const [val, setVal] = useState(String(Math.max(0, req.initial)));
  const [fresh, setFresh] = useState(true);
  const n = Math.min(99999, Number(val) || 0);

  const digit = useCallback((d: string) => {
    setVal((v) => {
      const base = fresh ? '' : v;
      const next = (base === '0' ? '' : base) + d;
      return next.length > 5 ? base || '0' : next;
    });
    setFresh(false);
  }, [fresh]);
  const back = useCallback(() => { setFresh(false); setVal((v) => (v.length <= 1 ? '0' : v.slice(0, -1))); }, []);
  const clear = useCallback(() => { setFresh(false); setVal('0'); }, []);
  const bump = useCallback((d: number) => { setFresh(false); setVal((v) => String(Math.max(0, (Number(v) || 0) + d))); }, []);
  const done = useCallback(() => { req.onDone(n); onClose(); }, [req, n, onClose]);

  // Fizyczna klawiatura (panel admina, klawiatura USB przy stacji) też działa.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); digit(e.key); }
      else if (e.key === 'Backspace') { e.preventDefault(); back(); }
      else if (e.key === 'Delete') { e.preventDefault(); clear(); }
      else if (e.key === 'Enter') { e.preventDefault(); done(); }
      else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === '+' || e.key === 'ArrowUp') { e.preventDefault(); bump(1); }
      else if (e.key === '-' || e.key === 'ArrowDown') { e.preventDefault(); bump(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [digit, back, clear, done, bump, onClose]);

  return (
    <div className="mg-scrim center mg-kp-scrim" onPointerDown={(e) => { if (e.target === e.currentTarget) pressClose(onClose)(e); }}>
      <div className="mg-keypad" role="dialog" aria-modal="true" aria-label={req.title}>
        <div className="mg-kp-head">
          {req.icon && <span className="mg-kp-ico"><Icon name={req.icon} size={20} /></span>}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="mg-kp-title">{req.title}</div>
            {req.sub && <div className="mg-kp-sub">{req.sub}</div>}
          </div>
          <button type="button" className="mg-x" onPointerDown={pressClose(onClose)} aria-label="Zamknij"><Icon name="x" size={20} /></button>
        </div>
        <div className="mg-kp-display">
          <button type="button" className="mg-kp-step" onPointerDown={press(() => bump(-1))} aria-label="Mniej"><Icon name="minus" size={22} /></button>
          <div className={`mg-kp-val tnum${fresh ? ' fresh' : ''}`}>{n}</div>
          <button type="button" className="mg-kp-step" onPointerDown={press(() => bump(1))} aria-label="Więcej"><Icon name="plus" size={22} /></button>
        </div>
        {req.describe && <div className="mg-kp-desc">{req.describe(n)}</div>}
        {req.suggestions && req.suggestions.length > 0 && (
          <div className="mg-kp-sugg">
            {req.suggestions.map((s) => (
              <button type="button" key={s.label} className="mg-kp-chip" onPointerDown={press(() => { setVal(String(s.value)); setFresh(false); })}>
                {s.label}
              </button>
            ))}
          </div>
        )}
        <div className="mg-kp-grid">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button type="button" key={d} className="mg-kp-key" onPointerDown={press(() => digit(d))}>{d}</button>
          ))}
          <button type="button" className="mg-kp-key fn" onPointerDown={press(clear)} aria-label="Wyczyść">C</button>
          <button type="button" className="mg-kp-key" onPointerDown={press(() => digit('0'))}>0</button>
          <button type="button" className="mg-kp-key fn" onPointerDown={press(back)} aria-label="Usuń cyfrę"><Icon name="delete" size={24} /></button>
        </div>
        <button type="button" className="mg-btn success block lg" onPointerDown={pressClose(done)}>
          <Icon name="check" size={20} /> {req.confirmLabel || 'Gotowe'}
        </button>
      </div>
    </div>
  );
}

/* ---------- historia ruchów ---------- */

function entryTitle(e: StockHistoryEntry): string {
  const units = Math.abs(e.totalDelta);
  const base = (() => {
    switch (e.kind) {
      case 'delivery': return `Dostawa · ${units} szt.`;
      case 'restock': return `Dobrano · ${units} szt.`;
      case 'consume': return e.orderName ? `Spakowane ${e.orderName}` : `Spakowane · ${units} szt.`;
      case 'remove': return e.reason ? `Zdjęte · ${e.reason}` : `Zdjęte · ${units} szt.`;
      case 'adjust': return `Inwentaryzacja · ${signed(e.totalDelta)} szt.`;
      default: return String(e.kind);
    }
  })();
  return e.isUndo ? `Cofnięto: ${base}` : base;
}

export function HistorySheet({ onClose, onChanged, notify }: {
  onClose: () => void;
  onChanged: () => void;
  notify: (msg: string, type?: 'ok' | 'err' | 'info') => void;
}) {
  const { data, isLoading, isError, refetch } = useStockHistory({ limit: 40 });
  const [more, setMore] = useState<StockHistoryEntry[]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [arm, setArm] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (armTimer.current) clearTimeout(armTimer.current); }, []);

  const next = cursor === undefined ? data?.nextBefore ?? null : cursor;
  const entries = (data?.entries || []).concat(more.filter((m) => !(data?.entries || []).some((d) => d.batchId === m.batchId)));

  async function loadMore() {
    if (!next) return;
    setLoadingMore(true);
    try {
      const page = await fetchStockHistoryPage(next);
      setMore((m) => m.concat(page.entries));
      setCursor(page.nextBefore);
    } catch (e) {
      notify(stockErrorText(e), 'err');
    } finally {
      setLoadingMore(false);
    }
  }

  async function undo(e: StockHistoryEntry) {
    // Dwa dotknięcia: pierwsze uzbraja przycisk („Na pewno?"), drugie cofa.
    if (arm !== e.batchId) {
      setArm(e.batchId);
      if (armTimer.current) clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => setArm(null), 4000);
      return;
    }
    setArm(null);
    setBusy(e.batchId);
    try {
      await undoStockBatch(e.batchId);
      notify(`Cofnięto: ${KIND_META[e.kind].label.toLowerCase()}`, 'ok');
      onChanged();
      setMore([]);
      setCursor(undefined);
      await refetch();
    } catch (err) {
      notify(stockErrorText(err), 'err');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Sheet title="Historia magazynu" sub="Ruchy od ostatniego resetu i wcześniej" icon="history" onClose={onClose} wide>
      {isLoading ? (
        <div className="mg-hist">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="mg-skel" style={{ height: 64 }} />)}</div>
      ) : isError ? (
        <div className="mg-empty small">
          <Icon name="circle-alert" size={26} />
          <p>Nie udało się wczytać historii.</p>
          <button type="button" className="mg-btn ghost" onClick={() => refetch()}><Icon name="rotate-ccw" size={16} /> Spróbuj ponownie</button>
        </div>
      ) : entries.length === 0 ? (
        <div className="mg-empty small"><Icon name="history" size={26} /><p>Jeszcze nic się nie wydarzyło.</p></div>
      ) : (
        <div className="mg-hist">
          {entries.map((e) => {
            const meta = KIND_META[e.kind] || KIND_META.adjust;
            return (
              <div key={e.batchId} className={`mg-hrow${e.undone ? ' undone' : ''}`}>
                <span className={`mg-hico tone-${e.isUndo ? 'neutral' : meta.tone}`}><Icon name={e.isUndo ? 'undo-2' : meta.icon} size={18} /></span>
                <div className="mg-hmain">
                  <div className="mg-htitle">
                    <span>{entryTitle(e)}</span>
                    {e.undone && <span className="mg-pill neutral">Cofnięto</span>}
                  </div>
                  <div className="mg-hmeta">
                    {timePl(e.createdAt)}
                    {e.actorName ? ` · ${e.actorName}` : ''}
                    {e.stationId ? ` · ${e.stationId}` : ''}
                  </div>
                  <div className="mg-hlines">
                    {e.lines.slice(0, 8).map((l) => (
                      <span key={l.key} className={`mg-hl ${l.delta >= 0 ? 'plus' : 'minus'}`}>
                        {l.name} <b className="tnum">{signed(l.delta)}</b>
                      </span>
                    ))}
                    {e.lines.length > 8 && <span className="mg-hl">+{e.lines.length - 8} {plural(e.lines.length - 8, ['pozycja', 'pozycje', 'pozycji'])}</span>}
                  </div>
                </div>
                {e.canUndo && !e.undone && (
                  <button
                    type="button"
                    className={`mg-btn sm ${arm === e.batchId ? 'danger' : 'ghost'}`}
                    disabled={busy === e.batchId}
                    onClick={() => undo(e)}
                  >
                    <Icon name="undo-2" size={15} /> {arm === e.batchId ? 'Na pewno?' : 'Cofnij'}
                  </button>
                )}
              </div>
            );
          })}
          {next && (
            <button type="button" className="mg-btn ghost block" disabled={loadingMore} onClick={loadMore}>
              {loadingMore ? 'Wczytywanie…' : 'Pokaż starsze'}
            </button>
          )}
        </div>
      )}
    </Sheet>
  );
}

/* ---------- szczegóły produktu ---------- */

export function ItemSheet({ item, overview, onClose, onAction }: {
  item: StockItemView;
  overview: StockOverview;
  onClose: () => void;
  onAction: (mode: 'delivery' | 'remove' | 'count') => void;
}) {
  const t = item.today;
  const rows: Array<[string, string, number, string]> = [
    ['truck', 'Dostawa', t.delivered, 'plus'],
    ['clipboard-check', 'Dobrano', t.restocked, 'plus'],
    ['package-check', 'Spakowano', -t.consumed, 'minus'],
    ['package-minus', 'Zdjęto', -t.removed, 'minus'],
    ['scale', 'Korekta', t.adjusted, t.adjusted >= 0 ? 'plus' : 'minus'],
  ];
  const waiting = overview.orders.filter((o) => o.missing.some((m) => m.key === item.key));
  const variant = itemVariant(item);
  return (
    <Sheet
      title={itemTitle(item)}
      sub={<>{variant && <span>{variant} · </span>}{item.sku && <span className="mono">{item.sku}</span>}{item.barcode && <span className="mono"> · {item.barcode}</span>}</>}
      icon={itemIconName(item.icon)}
      onClose={onClose}
    >
      <div className="mg-is-nums">
        <div><span className="lbl">Na stanie</span><b className={`tnum${item.onHand < 0 ? ' neg' : ''}`}>{item.onHand}</b></div>
        <div><span className="lbl">Potrzeba</span><b className="tnum">{overview.demandAvailable ? item.demand : '—'}</b></div>
        {item.toTake > 0 ? (
          <div className="short"><span className="lbl">Do dobrania</span><b className="tnum">{item.toTake}</b></div>
        ) : (
          <div className="ok"><span className="lbl">Wolne</span><b className="tnum">{Math.max(0, item.free)}</b></div>
        )}
      </div>
      {item.onHand < 0 && (
        <div className="mg-note warn"><Icon name="triangle-alert" size={16} /> Spakowano więcej, niż wpisano — dopisz dostawę albo zrób inwentaryzację.</div>
      )}
      <div className="mg-is-sec">Dzisiaj</div>
      <div className="mg-is-rows">
        {rows.map(([ic, lbl, v, cls]) => (
          <div key={lbl} className={`mg-is-row${v === 0 ? ' zero' : ''}`}>
            <Icon name={ic} size={16} /> <span>{lbl}</span>
            <b className={`tnum ${v === 0 ? '' : cls}`}>{v === 0 ? '0' : signed(v)}</b>
          </div>
        ))}
      </div>
      {waiting.length > 0 && (
        <>
          <div className="mg-is-sec">Czekają na ten produkt</div>
          <div className="mg-is-orders">
            {waiting.slice(0, 12).map((o) => {
              const m = o.missing.find((x) => x.key === item.key);
              return <span key={o.orderId} className={`mg-pill ${o.status === 'short' ? 'err' : 'warn'}`}>{o.orderName}{m ? ` ×${m.quantity}` : ''}</span>;
            })}
            {waiting.length > 12 && <span className="mg-pill neutral">+{waiting.length - 12}</span>}
          </div>
        </>
      )}
      <div className="mg-is-actions">
        <button type="button" className="mg-btn success" onClick={() => onAction('delivery')}><Icon name="truck" size={18} /> Dostawa</button>
        <button type="button" className="mg-btn ghost" onClick={() => onAction('remove')}><Icon name="package-minus" size={18} /> Zdejmij</button>
        <button type="button" className="mg-btn ghost" onClick={() => onAction('count')}><Icon name="scale" size={18} /> Policz</button>
      </div>
    </Sheet>
  );
}

/* ---------- wybór pozycji (dobranie spoza listy) ---------- */

export function ItemPickerSheet({ items, exclude, onPick, onClose }: {
  items: StockItemView[];
  exclude: string[];
  onPick: (it: StockItemView) => void;
  onClose: () => void;
}) {
  const list = items.filter((i) => exclude.indexOf(i.key) < 0);
  return (
    <Sheet title="Dodaj pozycję" sub="Wziąłeś coś spoza listy? Wybierz produkt." icon="package-plus" onClose={onClose} wide>
      {list.length === 0 ? (
        <div className="mg-empty small"><Icon name="circle-check" size={26} /><p>Wszystkie produkty są już na liście.</p></div>
      ) : (
        <div className="mg-pick-grid">
          {list.map((it) => (
            <button type="button" key={it.key} className="mg-pick-opt" onClick={() => onPick(it)}>
              <span className="mg-ico sm tone-neutral"><Icon name={itemIconName(it.icon)} size={18} /></span>
              <span className="mg-po-text">
                <b>{itemTitle(it)}</b>
                {itemVariant(it) && <small>{itemVariant(it)}</small>}
              </span>
              <span className="mg-po-qty tnum">{it.onHand}</span>
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
}
